import { Workbox, messageSW } from "workbox-window";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    WorkboxLifecycleEvent,
    WorkboxLifecycleWaitingEvent,
} from "workbox-window/utils/WorkboxEvent";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface WorkboxOptions {
    serviceWorkerPath: string;
}

interface WorkboxHookState {
    isReadyToReload: boolean;
    hasUpdate: boolean;
    serviceWorker?: ServiceWorker;
    serviceWorkerState?: ServiceWorkerState;
}

interface UseWorkboxHook extends WorkboxHookState {
    isEnabled: boolean;
    sendServiceWorkerMessage: <T extends {}>(data: T) => void;
}

interface ServiceWorkerConfiguration {
    enabled: boolean;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const defaultState: WorkboxHookState = {
    isReadyToReload: false,
    hasUpdate: false,
    serviceWorker: undefined,
    serviceWorkerState: undefined,
};

const SERVICE_WORKER_UPDATE_INTERVAL_MS = 5 * 60 * 1000;

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Hook
// -----------------------------------------------------------------------------------------

export function useWorkbox(options: WorkboxOptions): UseWorkboxHook {
    const serviceworkerConfiguration = useRef<ServiceWorkerConfiguration>(
        getServiceWorkerConfiguration()
    );
    const serviceWorkerPathRef = useRef(options.serviceWorkerPath);

    const [
        { hasUpdate, isReadyToReload, serviceWorker, serviceWorkerState },
        setState,
    ] = useState(defaultState);

    const isServiceWorkerDisabled = () =>
        !serviceworkerConfiguration.current.enabled;

    const sendServiceWorkerMessage = useCallback(
        async <T extends {}>(data: T): Promise<any> => {
            if (serviceWorker != null) {
                await messageSW(serviceWorker, data);
            }
        },
        [serviceWorker]
    );

    useEffect(() => {
        if (isServiceWorkerDisabled()) {
            unregisterServiceWorker();
            return;
        }

        const currentWorkbox = new Workbox(serviceWorkerPathRef.current, {
            scope: "/",
        });

        if (currentWorkbox == null) {
            setState((prev) => ({
                ...prev,
                isLoading: false,
            }));
            return;
        }

        currentWorkbox.getSW().then((sw) => {
            if (sw == null) {
                return;
            }

            setState((prev) => ({
                ...prev,
                serviceWorker: sw,
                serviceWorkerState: sw.state,
            }));
        });

        const handleControlling = (event: WorkboxLifecycleEvent) => {
            if (event.isUpdate) {
                setState((prev) => ({
                    ...prev,
                    isReadyToReload: true,
                    serviceWorkerState: "installed",
                }));
            }
        };

        const handleWaiting = (event: WorkboxLifecycleWaitingEvent) => {
            setState((prev) => ({
                ...prev,
                hasUpdate: true,
                serviceWorker: event.sw ?? prev.serviceWorker,
                serviceWorkerState:
                    event.sw?.state ?? prev.serviceWorker?.state,
            }));
        };

        const handleStateChange = (event: WorkboxLifecycleEvent) => {
            setState((prev) => ({
                ...prev,
                serviceWorker: event.sw ?? prev.serviceWorker,
                serviceWorkerState:
                    event.sw?.state ?? prev.serviceWorker?.state,
            }));
        };

        currentWorkbox.addEventListener("controlling", handleControlling);

        // Add an event listener to detect when the registered
        // service worker has installed but is waiting to activate.
        // @ts-ignore
        currentWorkbox.addEventListener("externalwaiting", handleWaiting);
        currentWorkbox.addEventListener("waiting", handleWaiting);
        currentWorkbox.addEventListener("activated", handleStateChange);
        currentWorkbox.addEventListener("redundant", handleStateChange);

        let updateInterval: ReturnType<typeof setInterval> | undefined;
        let registration: ServiceWorkerRegistration | undefined;
        let isDisposed = false;

        const checkForUpdate = () => {
            registration?.update().catch(() => {
                // A later interval or visibility change will retry the update.
            });
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkForUpdate();
            }
        };

        currentWorkbox.register().then((currentRegistration) => {
            if (isDisposed || currentRegistration == null) {
                return;
            }

            registration = currentRegistration;
            checkForUpdate();
            updateInterval = setInterval(
                checkForUpdate,
                SERVICE_WORKER_UPDATE_INTERVAL_MS
            );
            document.addEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        });

        return function cleanup() {
            isDisposed = true;

            if (updateInterval != null) {
                clearInterval(updateInterval);
            }

            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );

            currentWorkbox.removeEventListener(
                "controlling",
                handleControlling
            );

            currentWorkbox.removeEventListener(
                // @ts-ignore
                "externalwaiting",
                handleWaiting
            );
            currentWorkbox.removeEventListener("waiting", handleWaiting);
            currentWorkbox.removeEventListener("activated", handleStateChange);
            currentWorkbox.removeEventListener("redundant", handleStateChange);
        };
    }, []);

    return {
        hasUpdate,
        isEnabled: serviceworkerConfiguration.current.enabled,
        isReadyToReload,
        sendServiceWorkerMessage,
        serviceWorker,
        serviceWorkerState,
    };
}

// #endregion Hook

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

/**
 * Provides a way to override and disable the service worker.
 */
function getServiceWorkerConfiguration(): ServiceWorkerConfiguration {
    if (!("serviceWorker" in navigator)) {
        return { enabled: false };
    }

    const enabled = process.env.REACT_APP_ENABLE_SERVICE_WORKER === "true";

    return { enabled };
}

function unregisterServiceWorker() {
    if (navigator.serviceWorker) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
            for (let registration of registrations) {
                registration.unregister();
            }
        });
    }
}

// #endregion Functions
